import type { EnumUnion } from '@dresscode-frontend/utility-types';
import { type BusinessPartnerOverviewStatus, businessPartnerOverviewStatusValues } from '@standandforce/api-contract';

/**
 * 取引先一覧のソート可能なカラム
 */
export const BusinessPartnerOrderBy = {
  BUSINESS_PARTNER_NAME: 'BUSINESS_PARTNER_NAME',
  BUSINESS_PARTNER_TYPE: 'BUSINESS_PARTNER_TYPE',
  BUSINESS_PARTNER_ACTIVE_PERIOD: 'BUSINESS_PARTNER_ACTIVE_PERIOD',
  BUSINESS_PARTNER_REGISTRATION_DATE: 'BUSINESS_PARTNER_REGISTRATION_DATE',
  BUSINESS_PARTNER_TERMINATION_DATE: 'BUSINESS_PARTNER_TERMINATION_DATE'
} as const satisfies Record<string, string>;
export type BusinessPartnerOrderBy = EnumUnion<typeof BusinessPartnerOrderBy>;

/**
 * 取引先の役割
 */
export const BusinessPartnerRole = {
  CUSTOMER: 'CUSTOMER',
  SUPPLIER: 'SUPPLIER',
  FINANCIAL_INSTITUTION: 'FINANCIAL_INSTITUTION',
  RESELLER_AGENT: 'RESELLER_AGENT',
  EMPLOYEE: 'EMPLOYEE'
} as const;
export type BusinessPartnerRole = (typeof BusinessPartnerRole)[keyof typeof BusinessPartnerRole];

/**
 * 取引先のタイプ
 */
export const BusinessPartnerType = {
  CORPORATION: 'CORPORATION',
  INDIVIDUAL: 'INDIVIDUAL'
  // TODO: GROUPの定義が確定してから対応
  // GROUP: "GROUP",
} as const;
export type BusinessPartnerType = (typeof BusinessPartnerType)[keyof typeof BusinessPartnerType];

// 取引先ステータスは共通ライフサイクル7値を @standandforce/api-contract から取得する
// （businessPartnerOverviewStatusValues / BusinessPartnerOverviewStatus）。FE ローカルで
// enum を再宣言しない。

/** 非 TERMINATED の全ステータス。ALL タブ・既定表示・集計で使う。 */
const BUSINESS_PARTNER_NON_TERMINATED_STATUSES: readonly BusinessPartnerOverviewStatus[] = businessPartnerOverviewStatusValues.filter(status => status !== 'TERMINATED');

/**
 * 検索パラメータのステータス集合を、一覧・集計・CSV クエリ用の明示的な値へ展開する。
 * 空（= ALL タブ）は非 TERMINATED の全ステータスへ展開する。
 */
export function resolveBusinessPartnerStatusFilter(statusIn: readonly BusinessPartnerOverviewStatus[] | undefined): BusinessPartnerOverviewStatus[] {
  if (!statusIn || statusIn.length === 0) {
    return [...BUSINESS_PARTNER_NON_TERMINATED_STATUSES];
  }
  return [...statusIn];
}

/**
 * 法人のステータス
 */
export const CorporateStatus = {
  ACTIVE: 'ACTIVE',
  DORMANT: 'DORMANT',
  DISSOLVED: 'DISSOLVED'
} as const;
export type CorporateStatus = (typeof CorporateStatus)[keyof typeof CorporateStatus];

/**
 * 法人のタイプ
 */
// TODO: 法人形態は仕様確定してから対応
// export const CorporateType = {
//   CORPORATION: "CORPORATION",
//   GENERAL_PARTNERSHIP: "GENERAL_PARTNERSHIP",
//   LIMITED_PARTNERSHIP: "LIMITED_PARTNERSHIP",
//   JOINT_VENTURE: "JOINT_VENTURE",
//   GENERAL_ASSOCIATION: "GENERAL_ASSOCIATION",
//   GENERAL_FOUNDATION: "GENERAL_FOUNDATION",
//   PUBLIC_ASSOCIATION: "PUBLIC_ASSOCIATION",
//   PUBLIC_FOUNDATION: "PUBLIC_FOUNDATION",
//   NPO: "NPO",
//   OTHER: "OTHER",
// } as const;

// export type CorporateType = (typeof CorporateType)[keyof typeof CorporateType];

/**
 * テーブルカラムの定義
 */
export const BusinessPartnerTableColumns = {
  businessPartnerName: 'businessPartnerName',
  businessPartnerTypeLabel: 'businessPartnerTypeLabel',
  businessPartnerRoles: 'businessPartnerRoles',
  businessPartnerStatusLabel: 'businessPartnerStatusLabel',
  businessPartnerActivePeriod: 'businessPartnerActivePeriod',
  businessPartnerAddress: 'businessPartnerAddress',
  businessPartnerRegistrationDate: 'businessPartnerRegistrationDate',
  businessPartnerTerminationDate: 'businessPartnerTerminationDate'
} as const;
export type BusinessPartnerTableColumns = (typeof BusinessPartnerTableColumns)[keyof typeof BusinessPartnerTableColumns];